// Ported from https://github.com/hautaiou/saas-ml-algorithms/blob/dev/hautai/saas/algorithms/face/detector/utils.py

import { createCanvas } from "@/libs/canvas"
import { getImageSourceDimensions, imageBitmapToPixels, type ImageSource } from "@/libs/image"
import type { FaceCropperBoundingBox, FaceDetection, FramePixels, Landmarks } from "../types"

export function findCenterOfMass(
  points: Landmarks,
  imageShape: [number, number],
): [number, number] {
  const [height, width] = imageShape
  const numPoints = points.length
  const sumX = points.reduce((acc, point) => acc + point.x, 0)
  const sumY = points.reduce((acc, point) => acc + point.y, 0)
  const centerX = sumX / numPoints
  const centerY = sumY / numPoints
  return [centerX, centerY]
}

export function imgShapeToBbox(imageShape: [number, number]): [number, number][] {
  const [height, width] = imageShape
  return [
    [0, 0],
    [width, 0],
    [width, height],
    [0, height],
  ]
}

export function findSafePaddings(
  eyeAngleDegrees: number,
  imageHeight: number,
  imageWidth: number,
): [number, number] {
  const bbox = imgShapeToBbox([imageHeight, imageWidth])
  const inputCenter = [imageWidth / 2.0, imageHeight / 2.0]
  const angle = (eyeAngleDegrees * Math.PI) / 180
  const cosAngle = Math.cos(angle)
  const sinAngle = Math.sin(angle)
  const matrix = [
    [cosAngle, -sinAngle],
    [sinAngle, cosAngle],
  ]

  const transformedBbox = bbox.map(([x, y]) => {
    const xShifted = x - inputCenter[0]
    const yShifted = y - inputCenter[1]
    return [
      matrix[0][0] * xShifted + matrix[0][1] * yShifted,
      matrix[1][0] * xShifted + matrix[1][1] * yShifted,
    ]
  })

  const xs = transformedBbox.map(([x]) => x)
  const ys = transformedBbox.map(([, y]) => y)
  const wNew = Math.ceil(Math.max(...xs)) - Math.floor(Math.min(...xs))
  const hNew = Math.ceil(Math.max(...ys)) - Math.floor(Math.min(...ys))

  const paddingX = Math.max(0, Math.floor((wNew - imageWidth) / 2))
  const paddingY = Math.max(0, Math.floor((hNew - imageHeight) / 2))
  return [paddingY, paddingX]
}

const FACE_OVAL_POINT_INDICES = [
  10, 338, 338, 297, 297, 332, 332, 284, 284, 251, 251, 389, 389, 356, 356, 454, 454, 323, 323, 361,
  361, 288, 288, 397, 397, 365, 365, 379, 379, 378, 378, 400, 400, 377, 377, 152, 152, 148, 148,
  176, 176, 149, 149, 150, 150, 136, 136, 172, 172, 58, 58, 132, 132, 93, 93, 234, 234, 127, 127,
  162, 162, 21, 21, 54, 54, 103, 103, 67, 67, 109, 109, 10,
]

/**
 * Calculate the X offset needed to center the face in the bounding box.
 * Returns box_center_x - face_center_x (matching Python implementation):
 * - Positive value: face is to the LEFT of box center, bbox should move LEFT
 * - Negative value: face is to the RIGHT of box center, bbox should move RIGHT
 *
 * box_center_x = box_width // 2
 * if center_of_mass is None:
 *     center_of_mass = find_center_of_mass(facial_mesh[FACE_OVAL_POINT_INDICES], (box_height, box_width))
 * face_center_x, _ = center_of_mass
 * return box_center_x - face_center_x
 */
export function calculateBoxXShift(
  boundingBox: {
    x: number
    y: number
    width: number
    height: number
  },
  facialMesh: Landmarks,
  centerOfMass: [number, number] | null = null,
): number {
  const { x: boxX, y: boxY, width, height } = boundingBox
  const boxWidth = width

  const boxCenterX = boxWidth / 2
  const points = FACE_OVAL_POINT_INDICES.map((index) => facialMesh[index])

  if (!centerOfMass) {
    centerOfMass = findCenterOfMass(points, [height, width])
  }

  // The findCenterOfMass returns absolute coordinates
  // We need to convert face center to be relative to the bounding box
  const [faceCenterXAbsolute, ___] = centerOfMass
  const faceCenterXRelative = faceCenterXAbsolute - boxX

  // Calculate shift needed to center the face in the box
  const shift = boxCenterX - faceCenterXRelative

  return shift
}

export function findAngleBetweenEyes(
  facialMesh: Landmarks,
  imageHeight: number,
  imageWidth: number,
): number {
  const imageShape = [imageHeight, imageWidth] as [number, number]

  const leftEyeIndices = [
    133, 173, 157, 158, 159, 160, 161, 246, 33, 7, 163, 144, 145, 153, 154, 155,
  ]

  const rightEyeIndices = [
    362, 398, 384, 385, 386, 387, 388, 466, 263, 249, 390, 373, 374, 380, 381, 382,
  ]

  const leftEyePoints = leftEyeIndices.map((index) => facialMesh[index])
  const rightEyePoints = rightEyeIndices.map((index) => facialMesh[index])

  const leftEyeCenter = findCenterOfMass(leftEyePoints, imageShape)
  const rightEyeCenter = findCenterOfMass(rightEyePoints, imageShape)

  const xLeg = rightEyeCenter[0] - leftEyeCenter[0]
  const yLeg = rightEyeCenter[1] - leftEyeCenter[1]

  const angleRadians = Math.atan2(yLeg, xLeg)
  return angleRadians
}

export const rotateImage = (
  canvas: OffscreenCanvas | HTMLCanvasElement,
  angleRadians: number,
): OffscreenCanvas | HTMLCanvasElement => {
  const { width, height } = canvas

  const rotatedCanvas = createCanvas(width, height)
  const ctx = rotatedCanvas.getContext("2d", { alpha: true })!

  ctx.fillStyle = "black"
  ctx.fillRect(0, 0, width, height)

  ctx.translate(width / 2, height / 2)
  ctx.rotate(-angleRadians)
  ctx.drawImage(canvas, -width / 2, -height / 2)

  return rotatedCanvas
}

export function multiplyMatrixAndPoint(matrix: number[][], point: number[]): number[] {
  const [m0, m1, m2] = matrix
  const [x, y, z] = point
  const xNew = m0[0] * x + m0[1] * y + m0[2] * z
  const yNew = m1[0] * x + m1[1] * y + m1[2] * z
  const zNew = m2[0] * x + m2[1] * y + m2[2] * z
  return [xNew, yNew, zNew]
}

const FIX_FOREHEAD_POINTS: Array<[number[], number]> = [
  [[67, 109, 10, 338, 297], 1.0],
  [[103, 332, 104, 69, 108, 151, 337, 299, 333], 0.5],
  [[67, 109, 10, 338, 297], 0.0],
]

export function fixForeheadPoints(landmarks: Landmarks): Landmarks {
  const adjustedLandmarks = landmarks.map((point) => ({ ...point }))

  const height = landmarks[151].y - landmarks[10].y

  FIX_FOREHEAD_POINTS.forEach(([pointIndices, adjustCoeff]) => {
    const offset = height * adjustCoeff
    pointIndices.forEach((index) => {
      if (index < adjustedLandmarks.length) {
        adjustedLandmarks[index].y -= offset
      }
    })
  })

  return adjustedLandmarks
}

export const calculateFaceBoundingBox = (
  faceLandmarks: Landmarks,
  width: number,
  height: number,
): FaceCropperBoundingBox => {
  const adjustedLandmarks = fixForeheadPoints(faceLandmarks)

  let minX = Infinity,
    minY = Infinity
  let maxX = -Infinity,
    maxY = -Infinity

  adjustedLandmarks.forEach((landmark) => {
    const x = landmark.x * width
    const y = landmark.y * height

    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  })

  const bboxWidth = maxX - minX
  const bboxHeight = maxY - minY

  const squareSize = Math.max(bboxWidth, bboxHeight)

  const centerX = (minX + maxX) / 2
  const centerY = (minY + maxY) / 2

  return { minX, minY, maxX, maxY, centerX, centerY, squareSize }
}

export const extractFacePixels = async (
  source: ImageSource,
  faceDetection: FaceDetection | null,
): Promise<FramePixels> => {
  if (!faceDetection || !faceDetection.boundingBox) {
    const fullBitmap = await createImageBitmap(source)
    return imageBitmapToPixels(fullBitmap)
  }

  const { x, y, width, height } = faceDetection.boundingBox
  const { width: srcW, height: srcH } = getImageSourceDimensions(source)

  const px = Math.floor(x * srcW)
  const py = Math.floor(y * srcH)
  const pw = Math.floor(width * srcW)
  const ph = Math.floor(height * srcH)

  // SHRINKING: factor <1.0 to crop closer to center
  const shrinkFactor = 0.8
  const dx = Math.floor((pw * (1 - shrinkFactor)) / 2)
  const dy = Math.floor((ph * (1 - shrinkFactor)) / 2)

  const cropX = Math.max(0, px + dx)
  const cropY = Math.max(0, py + dy)
  const cropW = Math.floor(pw * shrinkFactor)
  const cropH = Math.floor(ph * shrinkFactor)

  const finalCropW = Math.min(cropW, srcW - cropX)
  const finalCropH = Math.min(cropH, srcH - cropY)

  const faceBitmap = await createImageBitmap(source, cropX, cropY, finalCropW, finalCropH)

  let tw = faceBitmap.width
  let th = faceBitmap.height

  const canvas = createCanvas(tw, th)
  const ctx = canvas.getContext("2d")! as CanvasRenderingContext2D
  ctx.drawImage(faceBitmap, 0, 0, tw, th)
  const imageData = ctx.getImageData(0, 0, tw, th)

  return {
    data: new Uint8Array(imageData.data),
    width: tw,
    height: th,
    format: "RGBA",
  }
}
